import clsx from 'clsx';
import type { PlanState } from '@/hooks/use-streaming-v2';

interface PlanChecklistProps {
  plan: PlanState;
}

/**
 * Plan-then-execute checklist (directive §8.2, Phase 2 Session 4).
 *
 * Consumes the propose_plan synthetic tool's `plan_proposed` SSE event
 * (Phase 1 ships the tool + emit; Q7 Option A — chosen over the regex-on-
 * prose Option B feature flag in design.md). Ticks through as
 * `plan_step_complete` arrives.
 *
 * I3 invariant: tool-error mid-step renders the active step `errored`
 * (red ring + ⚠) without auto-skipping subsequent steps. The streaming hook
 * reducer (Session 1) preserves `plan.erroredStepId` across `plan_step_active`
 * transitions; the visual evidence persists.
 */
export function PlanChecklist({ plan }: PlanChecklistProps) {
  if (plan.steps.length === 0) return null;

  return (
    <div
      data-testid="plan-checklist"
      className="self-start border border-panel-border bg-panel-bg rounded-[3px] px-3 py-2 max-w-full"
    >
      <div className="text-micro font-mono text-text-muted tracking-[0.5px] mb-1.5">PLAN</div>
      <ol className="flex flex-col gap-1">
        {plan.steps.map((step) => {
          const isComplete = plan.completedStepIds.has(step.step_id);
          const isActive = plan.activeStepId === step.step_id;
          const isErrored = plan.erroredStepId === step.step_id;
          // I3: errored visual wins when both `active` and `errored` are true.
          const stateLabel = isErrored ? 'errored'
            : isComplete ? 'complete'
            : isActive ? 'active'
            : 'pending';
          return (
            <li
              key={step.step_id}
              data-step-id={step.step_id}
              data-step-state={stateLabel}
              className={clsx(
                'flex items-center gap-2 text-xs font-mono px-1 rounded-[2px]',
                isErrored && 'text-danger ring-1 ring-danger/40',
                !isErrored && isActive && 'text-accent ring-1 ring-accent/30',
                !isErrored && !isActive && isComplete && 'text-text-muted',
                !isErrored && !isActive && !isComplete && 'text-text-dim',
              )}
            >
              <span aria-hidden="true" className="w-3 inline-block">
                {isErrored ? '⚠' : isComplete ? '✓' : isActive ? '◆' : '○'}
              </span>
              <span className="flex-1">{step.label}</span>
              {step.tool && (
                <span className="text-text-dim text-micro tracking-[0.3px]">{step.tool}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
